import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import requestWithAuth from "services/httpClient";
import { rootURL } from "services/rootURL";
import { type User, useAuth } from "../../features/authContext";
import {
	getRefreshToken,
	onSignedOut,
	refreshTokens,
} from "../../services/authTokens";
import PreloadPage from "../PreloadPage/PreloadPage";

const PUBLIC_PATHS = new Set(["/", "/logged-out", "/reset-password"]);

export async function fetchCurrentUser(): Promise<User | null> {
	try {
		const response = await requestWithAuth<{
			email: string;
			isAdmin: boolean;
			isBusinessAdmin: boolean;
		}>("get", `${rootURL}auth/me`);
		const body = (await response).body;
		return {
			email: body.email,
			isAdmin: body.isAdmin,
			isBusinessAdmin: body.isBusinessAdmin,
		};
	} catch {
		return null;
	}
}

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
	const { user, setUser, clearUser } = useAuth();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const [authLoading, setAuthLoading] = useState(true);

	// Restores the session on a cold load. The access token only ever lives in
	// memory, so after a reload there is nothing to present until the stored
	// refresh token is exchanged for a new one.
	useEffect(() => {
		let cancelled = false;

		const restore = async () => {
			if (!getRefreshToken()) {
				if (!cancelled) setAuthLoading(false);
				return;
			}

			const refreshed = await refreshTokens();
			if (cancelled) return;

			if (!refreshed) {
				setAuthLoading(false);
				return;
			}

			const user = await fetchCurrentUser();
			if (cancelled) return;

			if (user) {
				setUser(user);
			}
			setAuthLoading(false);
		};

		restore();
		return () => {
			cancelled = true;
		};
	}, [setUser]);

	// authTokens raises this when a refresh is refused — an expired, revoked or
	// reused refresh token. Clearing here rather than at the call site means one
	// place handles it however the session ended.
	useEffect(() => {
		return onSignedOut(() => {
			clearUser();
			navigate("/logged-out");
		});
	}, [clearUser, navigate]);

	if (authLoading) {
		return <PreloadPage isLoading />;
	}

	// A restored session lands on the app, not on the sign-in form. Without this
	// an admin who was already signed in and opened "/" got the login page —
	// the guard below only ever pointed the other way, turning signed-out
	// visitors away from private paths, and nothing sent a signed-in one on.
	//
	// Placed after the authLoading gate so it reads the finished session rather
	// than the null the state starts with, which would bounce every cold load.
	if (user && pathname === "/") {
		return <Navigate to="/overview" replace />;
	}

	// Kept from the previous provider: a signed-out visitor deep-linking into
	// the app lands on the sign-in page rather than an empty shell. /reset-password
	// has to stay reachable — it is where the reset email points, and whoever
	// follows it is by definition someone who cannot sign in.
	//
	// `<Navigate>` rather than a bare navigate() call in the render body, which
	// is what this was. That call left the `return` below to run in the same
	// pass, so the private page still rendered once — mounting its components
	// and firing their queries — before the navigation took it away. Returning
	// the redirect skips that render entirely. `replace` keeps the path nobody
	// was allowed to see out of history, so Back does not aim at it again.
	if (!PUBLIC_PATHS.has(pathname) && !getRefreshToken()) {
		return <Navigate to="/logged-out" replace />;
	}

	return <>{children}</>;
};

export default AuthProvider;
